import {
  AnalyticsEventName,
  AnalyticsPageType,
  ShopifySalesChannel,
  getClientBrowserParameters,
  sendShopifyAnalytics,
} from "@shopify/hydrogen-react";
import type {
  ShopifyAddToCartPayload,
  ShopifyAnalyticsProduct,
  ShopifyPageViewPayload,
} from "@shopify/hydrogen-react";
import type { ConsentState } from "../consent/ConsentProvider";

export const SHOPIFY_ADD_TO_CART_EVENT = "shopify:analytics:add-to-cart";

const STOREFRONT_ID = "hono-pono-shop";
const DEFAULT_CURRENCY = "EUR";

type ConsentFlags = Pick<
  ConsentState,
  "analyticsAllowed" | "marketingAllowed" | "saleOfDataAllowed"
>;

export interface AddToCartAnalyticsDetail {
  products: ShopifyAnalyticsProduct[];
  totalValue: number;
}

interface BasePayloadArgs {
  consent: ConsentFlags;
  language?: ShopifyPageViewPayload["acceptedLanguage"];
}

type BasePayload = Pick<
  ShopifyPageViewPayload,
  | "hasUserConsent"
  | "shopId"
  | "currency"
  | "storefrontId"
  | "acceptedLanguage"
  | "shopifySalesChannel"
  | "analyticsAllowed"
  | "marketingAllowed"
  | "saleOfDataAllowed"
> &
  ReturnType<typeof getClientBrowserParameters>;

interface PageViewArgs extends BasePayloadArgs {
  storeDomain: string;
  pageType: ShopifyPageViewPayload["pageType"];
}

interface AddToCartArgs extends BasePayloadArgs {
  storeDomain: string;
  cartId: string;
  products: ShopifyAnalyticsProduct[];
  totalValue: number;
}

function getShopIdGid(): string | null {
  const shopId = import.meta.env.VITE_SHOPIFY_SHOP_ID;

  if (!shopId) return null;

  return `gid://shopify/Shop/${shopId}`;
}

function normalizeStoreDomain(storeDomain: string): string {
  return storeDomain.replace(/^https?:\/\//, "").replace(/\/$/, "");
}

function buildBasePayload({
  consent,
  language,
}: BasePayloadArgs): BasePayload | null {
  const shopId = getShopIdGid();

  if (!shopId || !consent.analyticsAllowed) {
    return null;
  }

  return {
    hasUserConsent: true,
    shopId,
    currency: DEFAULT_CURRENCY,
    storefrontId: STOREFRONT_ID,
    acceptedLanguage: language,
    shopifySalesChannel: ShopifySalesChannel.headless,
    analyticsAllowed: consent.analyticsAllowed,
    marketingAllowed: consent.marketingAllowed,
    saleOfDataAllowed: consent.saleOfDataAllowed,
    ...getClientBrowserParameters(),
  };
}

export function getAnalyticsPageType(
  pathname: string,
): ShopifyPageViewPayload["pageType"] {
  const segments = pathname.split("/").filter(Boolean);
  const routeSegments = segments.length > 0 ? segments.slice(1) : segments;

  if (routeSegments.length === 0) return AnalyticsPageType.home;
  if (routeSegments[0] === "product") return AnalyticsPageType.product;
  if (routeSegments[0] === "collections" && routeSegments[1]) {
    return AnalyticsPageType.collection;
  }
  if (routeSegments[0] === "collections")
    return AnalyticsPageType.listCollections;
  if (routeSegments[0] === "cart") return AnalyticsPageType.cart;
  if (routeSegments[0] === "search") return AnalyticsPageType.search;

  return AnalyticsPageType.page;
}

export function dispatchAddToCartAnalytics(detail: AddToCartAnalyticsDetail) {
  window.dispatchEvent(
    new CustomEvent<AddToCartAnalyticsDetail>(SHOPIFY_ADD_TO_CART_EVENT, {
      detail,
    }),
  );
}

export function sendPageViewAnalytics({
  consent,
  storeDomain,
  language,
  pageType,
}: PageViewArgs) {
  const basePayload = buildBasePayload({ consent, language });

  if (!basePayload) {
    return Promise.resolve();
  }

  return sendShopifyAnalytics(
    {
      eventName: AnalyticsEventName.PAGE_VIEW,
      payload: {
        ...basePayload,
        canonicalUrl: window.location.href,
        pageType,
      },
    },
    normalizeStoreDomain(storeDomain),
  );
}

export function sendAddToCartAnalytics({
  consent,
  storeDomain,
  language,
  cartId,
  products,
  totalValue,
}: AddToCartArgs) {
  const basePayload = buildBasePayload({ consent, language });

  if (!basePayload) {
    return Promise.resolve();
  }

  return sendShopifyAnalytics(
    {
      eventName: AnalyticsEventName.ADD_TO_CART,
      payload: {
        ...basePayload,
        cartId,
        products,
        totalValue,
      } satisfies ShopifyAddToCartPayload,
    },
    normalizeStoreDomain(storeDomain),
  );
}

export type ViewAnalyticsEvent =
  | {
      eventName: typeof AnalyticsEventName.PRODUCT_VIEW;
      products: Array<ShopifyAnalyticsProduct>;
      totalValue: number;
    }
  | {
      eventName: typeof AnalyticsEventName.COLLECTION_VIEW;
      collectionHandle: string;
      collectionId: string;
    }
  | {
      eventName: typeof AnalyticsEventName.SEARCH_VIEW;
      searchString: string;
    };

interface ViewArgs extends BasePayloadArgs {
  storeDomain: string;
  event: ViewAnalyticsEvent;
}

export function sendViewAnalytics({
  consent,
  storeDomain,
  language,
  event,
}: ViewArgs) {
  const basePayload = buildBasePayload({ consent, language });

  if (!basePayload) {
    return Promise.resolve();
  }

  const { eventName, ...details } = event;
  return sendShopifyAnalytics(
    {
      eventName,
      payload: {
        ...basePayload,
        canonicalUrl: window.location.href,
        pageType: getAnalyticsPageType(window.location.pathname),
        ...details,
      },
    },
    normalizeStoreDomain(storeDomain),
  );
}
